import { useState } from 'react';
import type { CreateUserBody } from '../../hooks/use-users';
import { assignableRoles } from './role-utils';
import type { CapabilityMap, RoleResponse } from '../../lib/api-client';

interface Props {
  roles: RoleResponse[];
  actorCapabilities: CapabilityMap;
  onCancel: () => void;
  onSubmit: (body: CreateUserBody) => Promise<void>;
}

export function InviteUserModal({ roles, actorCapabilities, onCancel, onSubmit }: Props) {
  // Only roles whose capabilities are a subset of the actor's are assignable.
  const options = assignableRoles(roles, actorCapabilities);
  const defaultRole = options.find((r) => r.slug === 'user') ?? options[0];
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState<string>(defaultRole?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !roleId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        email: email.trim(),
        role_id: roleId,
        display_name: displayName.trim() || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onCancel}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          padding: '18px 20px 20px',
          width: 420,
          maxWidth: 'calc(100vw - 40px)',
          boxShadow: 'var(--shadow)',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          Invite member
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 16 }}>
          They'll receive an email with a sign-in link.
        </div>

        <Label>Email address</Label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          placeholder="name@company.com"
          style={inputStyle}
        />

        <div style={{ height: 12 }} />

        <Label>Display name (optional)</Label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Jane Smith"
          style={inputStyle}
        />

        <div style={{ height: 12 }} />

        <Label>Role</Label>
        <select
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {options.length === 0 && <option value="">No assignable roles</option>}
          {options.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '6px 10px',
              background: 'rgba(192,57,43,0.06)',
              border: '1px solid rgba(192,57,43,0.18)',
              borderRadius: 4,
              fontSize: 11,
              color: 'var(--kind-failure)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={busy} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !email.trim()} style={primaryButtonStyle(busy || !email.trim())}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 20,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--text-soft)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontWeight: 600,
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  background: 'var(--bg)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  color: 'var(--text)',
  outline: 'none',
} satisfies React.CSSProperties;

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 14px',
    fontSize: 12,
    fontWeight: 500,
    background: 'var(--accent)',
    color: '#fff',
    border: 0,
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    fontFamily: 'inherit',
  };
}

const secondaryButtonStyle = {
  padding: '7px 14px',
  fontSize: 12,
  fontWeight: 500,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
} satisfies React.CSSProperties;
